export type ChatRequestMessageRole = "Assistant" | "User";
export type ChatRequestMessageType =
  | "Message"
  | "File"
  | "ActionPrompt"
  | "MessageActionPrompt";

export interface ChatRequest {
  messages: ChatRequestMessage[];
  // Optional page context sent by the integrated oraChat surfaces (launch-page
  // hero / data-page side panel). Mirrors the .NET ChatRequestContext (US25878);
  // additive. The full-page oraChat sends no page context, but DOES send this object
  // from turn 2 carrying only `genieConversationId` — so presence never identifies a
  // surface (resources.chat.api gates on page-context keys: view/filters/visuals/focus).
  context?: ChatRequestContext;
  // Durable-chat thread id, minted client-side (crypto.randomUUID() with the
  // dashes stripped = 32 hex — the .NET DTO regex-guards the format). TOP-LEVEL
  // to move in lockstep with ChatRequest.ThreadId; never a legacy group id.
  threadId?: string;
}

/** One chart / grid / KPI tile on the page, described by its SCHEMA only.
 *  No values, no row counts, no sample rows — see buildChatVisuals for why. */
export interface ChatRequestVisual {
  title?: string;
  kind?: "chart" | "grid" | "kpi";
  /** Measure/series names encoded on the tile. */
  measures?: string[];
  /** Dimension names the tile is grouped or sliced by. */
  dimensions?: string[];
}

/** What the user has SELECTED on the page — intent, not more data. Bounded — see
 *  buildChatFocus in ~/common/rail/railContext. */
export interface ChatRequestFocus {
  /** The KPI / measure the user selected or drilled into. */
  kpi?: string;
  /** The page's time-interval control: Monthly, Quarterly, TTM, YTD, CY. */
  interval?: string;
  /** Active filters, named — `filters` above carries the same state as display text. */
  filters?: { name: string; value: string }[];
}

export interface ChatRequestContext {
  view?: string;
  filters?: string[];
  /** What is on screen, so chat can resolve "this"/"here". Bounded — see
   *  buildChatVisuals in ~/common/rail/railContext. */
  visuals?: ChatRequestVisual[];
  /** What the user SELECTED, so "why did this drop" resolves against the chart in
   *  front of them. Context, never a command. */
  focus?: ChatRequestFocus;
  // Genie conversation id from the previous answer; useChatStream attaches it
  // automatically so follow-up questions keep their Genie-side context.
  genieConversationId?: string;
}

export interface PromptActionRequest {
  document_id: string;
  question: string;
}

export interface ActionPromptOption {
  id: string;
  action: string;
  description: string;
}

export interface ActionPrompt {
  prompt?: string;
  options?: ActionPromptOption[];
  promptFooter?: string;
}

// 28022 follow-up chip: the label is shown, the question is sent. Both are
// required — the host drops any pair missing either half — so neither is optional.
export interface ChatFollowUp {
  label: string;
  question: string;
}

export interface ChatRequestMessage {
  role: ChatRequestMessageRole;
  type: ChatRequestMessageType;
  message: string;
  status: "Pending" | "Success" | "Error";
  filenames?: string[];
  document_id?: string;
  document_url?: string;
  actionPrompt?: ActionPrompt;
  id?: string; // Optional unique identifier for the message
  // Set on turns restored from server-side history. They are rendered but never
  // sent back on a later message: the workflow endpoint owns the thread, so
  // echoing them would duplicate context it already has.
  restored?: boolean;
  // US25878 evals: per-answer Genie ids — present only on assistant messages the
  // Genie route answered; their presence gates the one-tap thumbs control.
  genieMessageId?: string;
  genieConversationId?: string;
  // oraChat charts: a self-contained Vega-Lite spec (rows embedded in
  // spec.data.values) that a Genie chart answer should render as a chart, below
  // the text. Present only on chart answers.
  chartSpec?: Record<string, unknown>;
  // Genie answer-cache: true when this turn was replayed from the Cosmos answer
  // cache (byte-identical) rather than generated. Lets the UI show a "cached"
  // affordance; cache hits carry no Genie ids by design.
  servedFromCache?: boolean;
  // 28022: up to 3 follow-up chips the host suggested for THIS answer. A chip
  // renders its `label` but SENDS its `question` — the question names the measure
  // so it routes; the short label alone routes to a decline (handoff §1). Present
  // only on assistant answers, and only on the useChatStream surfaces (rail/hero).
  followUps?: ChatFollowUp[];
}

// The fields this UI consumes from a /resources/chat/history turn. The workflow
// endpoint also sends intent, genie_conversation_id and turn_id, which the proxy
// passes through verbatim and the conversation does not read — notably
// genie_conversation_id, which must never travel back out. thread_id is read for
// thread-scoped restore; restored turns themselves are still NEVER re-sent
// (resume relies on the server-side thread-keyed Genie binding).
export interface ChatHistoryTurn {
  role?: string;
  content?: string;
  timestamp?: number;
  turn_id?: string;
  thread_id?: string;
}

export interface ChatHistoryResponse {
  turns?: ChatHistoryTurn[];
  /**
   * False when the read failed rather than returning nothing. `turns` is an empty
   * array in both cases, so without this a timed-out read is indistinguishable from
   * a user who has never chatted.
   */
  historyAvailable?: boolean;
  /** False when the history route is deliberately unconfigured — off, not broken. */
  enabled?: boolean;
}

// One row of /resources/chat/threads/api — wire-cased snake_case because the
// .NET proxy hands the workflow rows through verbatim. All optional: a legacy
// group synthesized at read time carries the same shape plus legacy:true.
export interface ChatThreadSummary {
  thread_id?: string;
  title?: string;
  title_source?: string;
  created_ts?: number;
  last_ts?: number;
  message_count?: number;
  legacy?: boolean;
}

export interface ChatThreadListResponse {
  threads?: ChatThreadSummary[];
  // The API's own report of whether threading is live. Distinct from the caller's
  // feature flags: the workflow host carries a second kill-switch, and a list
  // that is off must never render as a user with no chats.
  enabled?: boolean;
  // Rows the single page withheld. ADVISORY in both directions — the host probes
  // pre-threading turns under its own cap without reporting saturation (false
  // while more exist), and a page that fills the host's ceiling reports true even
  // when it is the whole list. Fit only for stating what IS shown.
  has_more?: boolean;
  // The true total under the same filter, counted server-side in Cosmos. Unlike
  // has_more (advisory) or the page length, this is exact — fit for an "N of M"
  // count in the panel. Optional: a pre-28020 host omits it (28022 §3).
  total?: number;
}

// 28022 opening chips: GET /resources/chat/suggestions?view= → { suggestions }.
// Always 200 (chips are decoration); `source` separates the caller's own recent
// questions from curated ones. Wire-cased single words — no casing skew on the hop.
export interface ChatSuggestion {
  text?: string;
  source?: "recent" | "curated";
}

export interface ChatSuggestionsResponse {
  suggestions?: ChatSuggestion[];
}

// US25878 evals: one-tap thumbs on a Genie answer (mirrors .NET ChatFeedbackRequest).
export interface ChatFeedbackRequest {
  genieConversationId: string;
  genieMessageId: string;
  rating: "POSITIVE" | "NEGATIVE";
}

export interface MessageInputOverride {
  message: string;
  documentId: string;
}

export const OraChatFileUploadErrors = {
  // Generic fallback for a failed upload request. The per-file skip reasons —
  // which must name their files — live in buildSkippedUploadNotice below.
  default: "File upload failed. Please check the file and try again.",
};

/**
 * Files a single attach couldn't take, grouped by why. A file lands in exactly
 * one group; format and size are checked before the cap, so a file is only
 * "overCap" when it would otherwise have been accepted.
 */
export interface SkippedUploads {
  badFormat: string[]; // not a PDF or DOCX
  tooLarge: string[]; // over fileUploadMaxSize
  overCap: string[]; // valid, but past MAX_FILES_IN_CHAT_CONTEXT
}

/**
 * US 27884 AC-4/AC-5: a skipped-file notice must NAME the files it skipped, and
 * when one selection is skipped for more than one reason, EVERY reason shows
 * with its own files — not just whichever was found last. Returns null when
 * nothing was skipped. Both surfaces (rail + page) render this same string.
 */
export const buildSkippedUploadNotice = (
  { badFormat, tooLarge, overCap }: SkippedUploads,
  fileUploadMaxSize: number
): string | null => {
  const named = (files: string[]) => files.join(", ");
  const parts: string[] = [];
  if (badFormat.length) {
    parts.push(
      `PDF and DOCX are the only accepted file types. Not attached: ${named(
        badFormat
      )}.`
    );
  }
  if (tooLarge.length) {
    parts.push(
      `Files must be ${formatFileSize(
        fileUploadMaxSize
      )} or smaller. Not attached: ${named(tooLarge)}.`
    );
  }
  if (overCap.length) {
    parts.push(
      `You can attach up to ${MAX_FILES_IN_CHAT_CONTEXT} documents. Not attached: ${named(
        overCap
      )}.`
    );
  }
  return parts.length ? parts.join(" ") : null;
};

// US 27844 AC9: the 2025 hardcoded post-upload prompt actions were removed here
// (FILE_PROMPT_ACTIONS, DEFAULT_FILE_PROMPT_ACTION_OPTIONS,
// DEFAULT_OPEN_COMPARE_PROMPT_ACTION_OPTIONS). Their list advertised tools that
// no longer exist (Quote Comparison, Underwriting Review) while hiding ones that
// do. Suggestions are now generated from the live registered-workflow list via
// useDocumentSuggestions, so no static action list can go stale again.

export const DEFAULT_POST_FILE_UPLOAD_PROMPT =
  "Continue with the following options to interact with your file:";
export const DEFAULT_POST_FILE_UPLOAD_PROMPT_FOOTER =
  "Note: To compare two documents, simply upload an additional file (up to two files at a time). You can attach another file or select one from the left panel.";

export const DEFAULT_FILE_OPEN_COMPARISON_MESSAGE = `Your files are ready!  \nExplore them by typing a request in the chat field.`;
export const DEFAULT_FILE_OPEN_COMPARISON_PROMPT = `Your request may be based on the following:`;
export const DEFAULT_FILE_OPEN_COMPARISON_PROMPT_FOOTER =
  "You can also type a custom request to interact with the documents and extract the insights you need.\n\n &nbsp; \n\n__Note__: Files in this chat context have already reached the maximum allowed of up to two documents.  \nPlease refresh oraChat page to start querying with a new set of documents.";

export const SINGLE_DOCUMENT_ATTACHMENT_MESSAGE = `
## Explore your document with these tools (Beta)
Easily analyze and interact with your document using these commands
 
- **Summarize**: Get a quick overview of your document
_Type:_ "Summarize this document"
_Purpose:_ Get key points of a document in seconds.
- **Ask Questions**: Find answers about your document.
_Type:_ "Ask [your question]" (e.g., "What is the policy's coverage for water damage?")
_Purpose:_ Get specific details without searching manually.
- **Extract Information**: Pull specific data in your preferred format.
_Type:_ "Extract [information] in [format]" (e.g., "Extract the list of items in CSV format")
_Purpose:_ Export data for easy use.
 
_Note:_ Replace text in [brackets] with your specific request.
`;
export const MULTIPLE_DOCUMENT_ATTACHMENT_MESSAGE = `
## Explore multiple documents with these tools (Beta)
Quickly analyze multiple documents with these tools:
 
- **Compare Documents**: Spot changes between documents
_Type:_ "Compare these documents"
_Purpose:_ Generate a downloadable report highlighting changes between the documents.
- **Cross Check Documents**: Check consistency across multiple documents
_Type:_ "Cross check these documents"
_Purpose:_ Generate a downloadable report highlighting the consistency and discrepancies between the documents.
`;
export interface ChatDocumentDto {
  timeBucket: string;
  fileId: number;
  filename: string;
  documentId: string;
  documentUrl: string;
  dateUploaded: Date | string;
}

export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) {
    const kb = bytes / 1024;
    return `${kb % 1 === 0 ? kb : kb.toFixed(1)} KB`;
  }
  const mb = bytes / 1048576;
  return `${mb % 1 === 0 ? mb : mb.toFixed(1)} MB`;
};

export const getAttachmentIconTooltip = (fileUploadMaxSize: number): string =>
  `Upload a file to interact with in an oraChat session. Only text-based PDFs and DOCX files are supported, with a maximum file size of ${formatFileSize(
    fileUploadMaxSize
  )}.`;

export const MAX_FILES_IN_CHAT_CONTEXT: number = 10;

/**
 * Max characters a user may type into ANY oraChat composer (US 27105). Single
 * source of truth so the dedicated page, the rail, and the launch hero cannot
 * drift apart — historically the page capped at 500 while the rail and hero
 * were uncapped (600+ accepted). Converged DOWN to the page's existing 500
 * (never up / uncapped) on purpose: no surface should ever send a LONGER prompt
 * to the mcp/Genie backend than it does today, which keeps this a pure
 * client-side change with zero backend coupling.
 */
export const MAX_MESSAGE_INPUT_CHAR_COUNT = 500;

/**
 * A message as it is actually SENT — trimmed, newlines removed. Every send path
 * normalizes this way (useChatStream, ChatPanel.handleMessageSubmission), so the
 * composer's over-limit gate + counter must measure THIS, not the raw input, or a
 * multi-line paste that sends within the cap would be wrongly blocked (Bug 27489).
 */
export const normalizeMessageForSend = (text: string): string =>
  text.trim().replaceAll("\n", "");

// Response types for /resources/chat/documents
export interface ChatDocumentMetadata {
  file_extension: string;
  text_length: number;
  processing_status: string;
}

export interface ChatDocumentData {
  document_id: string;
  file_name: string;
  content_type: string | null;
  file_size: number;
  chunk_count: number;
  created_at: string;
  blob_url: string;
  user_id: string;
  blob_storage_path: string;
  metadata: ChatDocumentMetadata;
  timeBucket?: string;
}

export interface ChatReportData {
  report_id: string;
  report_name: string;
  report_type: string | null;
  created_at: string;
  user_id: string;
  document_ids: string[];
  blob_url: string;
  blob_storage_path: string;
  metadata: ChatDocumentMetadata;
}

// Combines ChatDocumentData and ChatReportData, removing duplicate properties
export interface ChatFileData {
  id: string;
  name: string;
  type?: string;
  blob_url: string;
  created_at: string;
  user_id: string;
  timeBucket?: string;
  document_ids?: string[];
}

export interface ChatDocumentResponse {
  status: string;
  requestType: string;
  userId: string;
  data: {
    files?: ChatFileData[];
    documents?: ChatDocumentData[];
    reports?: ChatReportData[];
    total_count: number;
    user_id: string;
  };
}
